import React, { useEffect, useState } from 'react';
import { Alert, Button, Empty, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { errorMessage } from '../../api';
import type { BillingApi, EmailLogRow, EmailStatus } from '../../billingApi';
import { dayTime } from '../../lib/format';

const { Text } = Typography;

const PAGE_SIZE = 50;

export const EMAIL_STATUS: Record<EmailStatus, { label: string; color: string }> = {
  queued: { label: 'Queued', color: 'default' },
  sent: { label: 'Sent', color: 'blue' },
  delivered: { label: 'Delivered', color: 'green' },
  delayed: { label: 'Delayed', color: 'gold' },
  bounced: { label: 'Bounced', color: 'red' },
  complained: { label: 'Complained', color: 'volcano' },
  failed: { label: 'Failed', color: 'red' },
};

interface Query {
  templateKey: string | null;
  status: EmailStatus | null;
  search: string;
  page: number;
}

/** Every email sent or waiting to be sent, newest first. */
export const LogTab: React.FC<{ billing: BillingApi }> = ({ billing }) => {
  const [query, setQuery] = useState<Query>({
    templateKey: null,
    status: null,
    search: '',
    page: 1,
  });
  const [result, setResult] = useState<{ emails: EmailLogRow[]; total: number } | null>(null);
  const [loaded, setLoaded] = useState<Query | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [templateKeys, setTemplateKeys] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    billing
      .listTemplates()
      .then((t) => {
        if (!cancelled) setTemplateKeys(t.map((x) => x.key));
      })
      .catch(() => {
        /* the filter just stays empty; the table shows its own error */
      });
    return () => {
      cancelled = true;
    };
  }, [billing]);

  useEffect(() => {
    let cancelled = false;
    billing
      .listEmails({
        templateKey: query.templateKey,
        status: query.status,
        search: query.search,
        page: query.page,
        pageSize: PAGE_SIZE,
      })
      .then((r) => {
        if (cancelled) return;
        setResult(r);
        setLoaded(query);
        setLoadError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoaded(query);
        setLoadError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [billing, query, reloadKey]);

  const loading = loaded !== query && !loadError;
  const filtered = !!query.templateKey || !!query.status || !!query.search;

  const columns = [
    {
      title: 'When',
      key: 'created_at',
      width: 190,
      render: (_: unknown, r: EmailLogRow) => <Text type="secondary">{dayTime(r.created_at)}</Text>,
    },
    { title: 'To', dataIndex: 'to_email', key: 'to' },
    { title: 'Email', dataIndex: 'template_key', key: 'template' },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_: unknown, r: EmailLogRow) => {
        const meta = EMAIL_STATUS[r.status] ?? { label: r.status, color: 'default' };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Error',
      key: 'error',
      ellipsis: true,
      render: (_: unknown, r: EmailLogRow) =>
        r.error ? <Text type="danger">{r.error}</Text> : null,
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Search by address"
          aria-label="Search emails"
          style={{ width: 260 }}
          onSearch={(value) => setQuery((q) => ({ ...q, search: value.trim(), page: 1 }))}
        />
        <Select
          allowClear
          placeholder="All emails"
          aria-label="Email"
          style={{ width: 220 }}
          options={templateKeys.map((k) => ({ value: k, label: k }))}
          value={query.templateKey}
          onChange={(v) => setQuery((q) => ({ ...q, templateKey: v ?? null, page: 1 }))}
        />
        <Select
          allowClear
          placeholder="Any status"
          aria-label="Status"
          style={{ width: 160 }}
          options={(Object.keys(EMAIL_STATUS) as EmailStatus[]).map((s) => ({
            value: s,
            label: EMAIL_STATUS[s].label,
          }))}
          value={query.status}
          onChange={(v) => setQuery((q) => ({ ...q, status: v ?? null, page: 1 }))}
        />
        {result && !loadError && (
          <Text type="secondary">{result.total.toLocaleString('en')} in total</Text>
        )}
      </Space>

      {loadError && (
        <Alert
          type="error"
          showIcon
          title="Could not load the emails"
          description={loadError}
          action={<Button onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<EmailLogRow>
        rowKey="id"
        size="middle"
        loading={loading}
        dataSource={loadError ? [] : (result?.emails ?? [])}
        columns={columns}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: query.page,
          pageSize: PAGE_SIZE,
          total: result?.total ?? 0,
          showSizeChanger: false,
          hideOnSinglePage: true,
          onChange: (page) => setQuery((q) => ({ ...q, page })),
        }}
        locale={{
          emptyText: (
            <Empty
              description={filtered ? 'No email matches this search.' : 'No email sent yet.'}
            />
          ),
        }}
      />
    </div>
  );
};
